import { useEffect, useState } from 'react';
import { convertMetricOffsetToCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import { getStoreyElevationRange } from '@shared/helpers/design/storey.helpers';
import type { Coordinate } from '@shared/models/geography.model';
import type { Storey } from '@shared/models/design/storey.model';
import type { DesignMap } from '../map';
import {
  MapTooltipType,
  type MapTooltipPayload,
} from '../models/map-observables.models';
import { formatSegmentLengthMeters } from '../helpers/segment-length-label.helpers';

/** Facade heights share the editable measurement layer with polygon lengths. */
export const useFacadeStoreyHeightTooltips = (
  map: DesignMap | undefined,
  location: Coordinate,
  storeys: Storey[],
  selectedStoreyId: string | undefined,
): MapTooltipPayload[] => {
  const [labels, setLabels] = useState<MapTooltipPayload[]>([]);
  useEffect(() => {
    const instance = map?.map;
    if (!map || !instance || !selectedStoreyId) {
      setLabels((current) => (current.length ? [] : current));
      return;
    }
    const update = () => {
      const hoveredId = map.state.hoveredStoreyId$.getValue();
      const editingId = document.activeElement
        ?.closest('[data-storey-height-id]')
        ?.getAttribute('data-storey-height-id');
      const next: MapTooltipPayload[] = [];
      if (map.isExactFacadeMode) {
        const editableStoreys = map.state.storeys$
          .getValue()
          .filter(
            (storey) =>
              storey.height > 0 &&
              storey.shapes.some(
                (shape) => !shape.cutout && shape.points.length >= 3,
              ),
          );
        for (const [index, storey] of editableStoreys.entries()) {
          if (
            storey.id !== selectedStoreyId &&
            storey.id !== hoveredId &&
            storey.id !== editingId
          )
            continue;
          const { bottom, top } = getStoreyElevationRange(
            map.state.storeys$.getValue(),
            storey,
          );
          const points = storey.shapes
            .filter((shape) => !shape.cutout && shape.points.length >= 3)
            .flatMap((shape) => shape.points);
          const projected = points.flatMap((point) => {
            const result = map.projectFacadeCoordinate(
              convertMetricOffsetToCoordinate(location, point),
              (bottom + top) / 2,
            );
            return result ? [result] : [];
          });
          const firstPoint = projected[0];
          if (!firstPoint || storey.height <= 0) continue;
          next.push({
            type: MapTooltipType.Distance,
            tone: 'segment-length',
            anchor: 'right',
            screenPoint: [
              Math.min(...projected.map((point) => point[0])) - 12,
              firstPoint[1],
            ],
            text: `${formatSegmentLengthMeters(storey.height)} m`,
            storeyHeight: {
              storeyId: storey.id,
              heightMeters: storey.height,
              nextStoreyId:
                editableStoreys[(index + 1) % editableStoreys.length]?.id,
              previousStoreyId:
                editableStoreys[
                  (index - 1 + editableStoreys.length) % editableStoreys.length
                ]?.id,
            },
            selected: storey.id === selectedStoreyId,
          });
        }
      }
      setLabels((current) =>
        JSON.stringify(current) === JSON.stringify(next) ? current : next,
      );
    };
    const subscription = map.state.hoveredStoreyId$.subscribe(update);
    subscription.add(map.state.storeys$.subscribe(update));
    instance.on('render', update);
    instance.on('move', update);
    instance.on('resize', update);
    update();
    return () => {
      subscription.unsubscribe();
      instance.off('render', update);
      instance.off('move', update);
      instance.off('resize', update);
    };
  }, [map, location, storeys, selectedStoreyId]);
  return labels;
};
